import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import './AdminDashboard.css';

const AdminDashboard = ({ user, addNotification }) => {
    const [activePeriod, setActivePeriod] = useState('month');
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        stats: {
            totalBookings: 0,
            activeMembers: 0,
            pendingApprovals: 0,
            pendingItems: 0,
            facilityUtilization: 0,
            upcomingEvents: 0
        },
        bookingTrends: [],
        popularFacilities: [],
        statusDistribution: {},
        recentBookings: [],
        equipmentInventory: []
    });

    // Fetch all dashboard data
// Fetch all dashboard data with timeout
const fetchDashboardData = async () => {
    setLoading(true);
    
    // Add a timeout to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000);
    });
    
    try {
        console.log('🔍 Fetching dashboard data...');
        
        // Race the API calls against timeout
        const bookingsPromise = adminService.getAllBookings();
        const usersPromise = adminService.getAllUsers();
        const pendingMembersPromise = adminService.getPendingMembers();
        
        // Use Promise.allSettled to handle individual failures
        const results = await Promise.race([
            Promise.allSettled([bookingsPromise, usersPromise, pendingMembersPromise]),
            timeoutPromise
        ]);
        
        // Handle timeout
        if (results === undefined || (results.message && results.message.includes('timeout'))) {
            console.error('❌ Request timeout');
            // Show whatever we have or use empty data
            setDashboardData({
                stats: {
                    totalBookings: 0,
                    activeMembers: 0,
                    pendingApprovals: 0,
                    pendingItems: 0,
                    facilityUtilization: 0,
                    upcomingEvents: 0
                },
                bookingTrends: [],
                popularFacilities: [],
                statusDistribution: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
                recentBookings: [],
                equipmentInventory: []
            });
            if (addNotification) addNotification('Request timeout - showing partial data', 'warning');
            setLoading(false);
            return;
        }
        
        // Process results
        const bookingsRes = results[0]?.status === 'fulfilled' ? results[0]?.value : { success: false, bookings: [] };
        const usersRes = results[1]?.status === 'fulfilled' ? results[1]?.value : { success: false, users: [] };
        const pendingMembersRes = results[2]?.status === 'fulfilled' ? results[2]?.value : { success: false, users: [] };
        
        console.log('Bookings success:', bookingsRes?.success);
        console.log('Users success:', usersRes?.success);
        console.log('Pending members success:', pendingMembersRes?.success);
        
        const allBookings = bookingsRes?.bookings || [];
        const allUsers = usersRes?.users || [];
        const pendingMembers = pendingMembersRes?.users || [];
        
        const totalBookings = allBookings.length;
        const activeMembers = allUsers.filter(u => u.status === 'active').length;
        const pendingApprovals = pendingMembers.length;
        const pendingBookings = allBookings.filter(b => b.status === 'pending').length;
        const approvedBookings = allBookings.filter(b => b.status === 'approved').length;
        const rejectedBookings = allBookings.filter(b => b.status === 'rejected').length;
        const cancelledBookings = allBookings.filter(b => b.status === 'cancelled').length;
        
        const statusDistribution = {
            pending: pendingBookings,
            approved: approvedBookings,
            rejected: rejectedBookings,
            cancelled: cancelledBookings
        };
        
        const recentBookings = [...allBookings]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 6);
        
        const facilityCounts = {};
        allBookings.forEach(booking => {
            const facility = booking.facility_name || booking.location;
            if (facility) {
                facilityCounts[facility] = (facilityCounts[facility] || 0) + 1;
            }
        });
        const popularFacilities = Object.entries(facilityCounts)
            .map(([name, count]) => ({ facility_name: name, booking_count: count }))
            .sort((a, b) => b.booking_count - a.booking_count)
            .slice(0, 5);
        
        // Calculate booking trends based on selected period
        let trendsData = [];
        
        if (activePeriod === 'week') {
            for (let i = 6; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                const count = allBookings.filter(b => b.created_at?.split(' ')[0] === dateStr).length;
                trendsData.push({ 
                    date: dateStr, 
                    label: date.toLocaleDateString('en-US', { weekday: 'short' }),
                    total: count 
                });
            }
        } else if (activePeriod === 'month') {
            for (let i = 29; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                const count = allBookings.filter(b => b.created_at?.split(' ')[0] === dateStr).length;
                if (i % 5 === 0 || i === 0) {
                    trendsData.push({ 
                        date: dateStr, 
                        label: date.getDate().toString(),
                        total: count 
                    });
                }
            }
        } else if (activePeriod === 'quarter') {
            const weeklyData = {};
            for (let i = 89; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const weekNum = Math.floor((date.getDate() - 1) / 7) + 1;
                const weekKey = `Week ${weekNum}`;
                const count = allBookings.filter(b => b.created_at?.split(' ')[0] === date.toISOString().split('T')[0]).length;
                weeklyData[weekKey] = (weeklyData[weekKey] || 0) + count;
            }
            trendsData = Object.entries(weeklyData).map(([label, total]) => ({ label, total }));
        } else if (activePeriod === 'year') {
            const monthlyData = {};
            for (let i = 11; i >= 0; i--) {
                const date = new Date();
                date.setMonth(date.getMonth() - i);
                const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
                const count = allBookings.filter(b => {
                    const bookingDate = new Date(b.created_at);
                    return bookingDate.getMonth() === date.getMonth() && 
                           bookingDate.getFullYear() === date.getFullYear();
                }).length;
                monthlyData[monthKey] = (monthlyData[monthKey] || 0) + count;
            }
            trendsData = Object.entries(monthlyData).map(([label, total]) => ({ label, total }));
        }
        
        const newData = {
            stats: {
                totalBookings,
                activeMembers,
                pendingApprovals,
                pendingItems: pendingBookings,
                facilityUtilization: totalBookings > 0 ? Math.round((approvedBookings / totalBookings) * 100) : 0,
                upcomingEvents: 0
            },
            bookingTrends: trendsData,
            popularFacilities,
            statusDistribution,
            recentBookings,
            equipmentInventory: []
        };
        
        setDashboardData(newData);
        
        if (addNotification) addNotification('Dashboard loaded successfully', 'success');
        
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        
        // Set empty data so page shows something
        setDashboardData({
            stats: {
                totalBookings: 0,
                activeMembers: 0,
                pendingApprovals: 0,
                pendingItems: 0,
                facilityUtilization: 0,
                upcomingEvents: 0
            },
            bookingTrends: [],
            popularFacilities: [],
            statusDistribution: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
            recentBookings: [],
            equipmentInventory: []
        });
        
        if (addNotification) addNotification('Failed to load dashboard data: ' + error.message, 'error');
    } finally {
        setLoading(false);
    }
};
    useEffect(() => {
        fetchDashboardData();
    }, [activePeriod]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return '#10b981';
            case 'pending': return '#f59e0b';
            case 'rejected': return '#ef4444';
            case 'cancelled': return '#6b7280';
            default: return '#00843D';
        }
    };

// Even if not loading but no data, show something
if (!dashboardData || !dashboardData.stats) {
    return (
        <div className="dashboard-error">
            <i className="fa-solid fa-circle-exclamation"></i>
            <h3>Unable to load dashboard</h3>
            <p>Please check your internet connection and try again.</p>
            <button onClick={fetchDashboardData} className="retry-btn">
                <i className="fa-solid fa-rotate-right"></i> Retry
            </button>
        </div>
    );
}
    return (
        <div className="admin-dashboard">
            {/* Hero Header Section */}
           <div className="executive-hero-section">
    <div className="executive-hero-content">
        <div className="executive-badge">
            <i className="fa-solid fa-crown"></i>
            <span>Executive Portal</span>
        </div>
        <h1 className="executive-title">Dashboard</h1>
        <p className="executive-description">Welcome back, {user?.first_name || 'Administrator'} — here's your real-time club performance overview.</p>
    </div>
    <div className="executive-actions">
        <button className="executive-refresh-btn" onClick={fetchDashboardData}>
            <i className="fa-solid fa-rotate-right"></i> Sync Data
        </button>
    </div>
</div>

            {/* Period Tabs - Modern Design */}
            <div className="period-tabs-container">
                <div className="period-tabs">
                    <button 
                        className={activePeriod === 'week' ? 'active' : ''}
                        onClick={() => setActivePeriod('week')}
                    >
                        <i className="fa-solid fa-calendar-week"></i>
                        <span>Weekly</span>
                    </button>
                    <button 
                        className={activePeriod === 'month' ? 'active' : ''}
                        onClick={() => setActivePeriod('month')}
                    >
                        <i className="fa-solid fa-calendar-month"></i>
                        <span>Monthly</span>
                    </button>
                    <button 
                        className={activePeriod === 'quarter' ? 'active' : ''}
                        onClick={() => setActivePeriod('quarter')}
                    >
                        <i className="fa-solid fa-chart-simple"></i>
                        <span>Quarterly</span>
                    </button>
                    <button 
                        className={activePeriod === 'year' ? 'active' : ''}
                        onClick={() => setActivePeriod('year')}
                    >
                        <i className="fa-solid fa-calendar-year"></i>
                        <span>Yearly</span>
                    </button>
                </div>
            </div>

            {/* Stats Grid - Modern Cards */}
            <div className="stats-grid-modern">
                <div className="stat-card-modern">
                    <div className="stat-icon-wrapper">
                        <div className="stat-icon-circle total">
                            <i className="fa-solid fa-calendar-check"></i>
                        </div>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value-modern">{dashboardData.stats.totalBookings.toLocaleString()}</span>
                        <span className="stat-label-modern">Total Bookings</span>
                    </div>
                    <div className="stat-trend positive">
                        <i className="fa-solid fa-arrow-up"></i>
                        <span>+12%</span>
                    </div>
                </div>

                <div className="stat-card-modern">
                    <div className="stat-icon-wrapper">
                        <div className="stat-icon-circle active">
                            <i className="fa-solid fa-users"></i>
                        </div>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value-modern">{dashboardData.stats.activeMembers.toLocaleString()}</span>
                        <span className="stat-label-modern">Active Members</span>
                    </div>
                    <div className="stat-trend positive">
                        <i className="fa-solid fa-arrow-up"></i>
                        <span>+8%</span>
                    </div>
                </div>

                <div className="stat-card-modern">
                    <div className="stat-icon-wrapper">
                        <div className="stat-icon-circle pending">
                            <i className="fa-solid fa-clock"></i>
                        </div>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value-modern">{dashboardData.stats.pendingApprovals}</span>
                        <span className="stat-label-modern">Pending Approvals</span>
                    </div>
                    <div className="stat-trend neutral">
                        <i className="fa-solid fa-minus"></i>
                        <span>Awaiting</span>
                    </div>
                </div>

                <div className="stat-card-modern">
                    <div className="stat-icon-wrapper">
                        <div className="stat-icon-circle utilization">
                            <i className="fa-solid fa-chart-line"></i>
                        </div>
                    </div>
                    <div className="stat-content">
                        <span className="stat-value-modern">{dashboardData.stats.facilityUtilization}%</span>
                        <span className="stat-label-modern">Approval Rate</span>
                    </div>
                    <div className="stat-trend positive">
                        <i className="fa-solid fa-arrow-up"></i>
                        <span>+5%</span>
                    </div>
                </div>
            </div>

            {/* Main Dashboard Layout - Two Column Redesign */}
            <div className="dashboard-grid-modern">
                {/* Left Column */}
                <div className="dashboard-left-col">
                    {/* Booking Trends - Enhanced Chart */}
                    <div className="dashboard-card-modern chart-card">
                        <div className="card-header-modern">
                            <div className="header-title">
                                <i className="fa-solid fa-chart-line"></i>
                                <h3>Booking Trends</h3>
                            </div>
                            <div className="period-indicator">
                                <i className="fa-regular fa-calendar"></i>
                                <span>{activePeriod === 'week' ? 'Last 7 Days' : 
                                         activePeriod === 'month' ? 'Last 30 Days' : 
                                         activePeriod === 'quarter' ? 'Quarterly View' : 'Annual View'}</span>
                            </div>
                        </div>
                        <div className="trends-chart-modern">
                            {dashboardData.bookingTrends.length > 0 ? (
                                dashboardData.bookingTrends.map((item, index) => {
                                    const maxValue = Math.max(...dashboardData.bookingTrends.map(t => t.total), 1);
                                    const height = (item.total / maxValue) * 140;
                                    return (
                                        <div key={index} className="trend-bar-modern">
                                            <div className="bar-container">
                                                <div className="bar-fill-modern" style={{ height: `${height}px` }}>
                                                    <span className="bar-value-modern">{item.total}</span>
                                                </div>
                                            </div>
                                            <span className="bar-label-modern">{item.label || formatDate(item.date)}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="no-data">No booking data available</div>
                            )}
                        </div>
                    </div>

                    {/* Popular Facilities - Enhanced */}
                    <div className="dashboard-card-modern facilities-card">
                        <div className="card-header-modern">
                            <div className="header-title">
                                <i className="fa-solid fa-trophy"></i>
                                <h3>Most Requested Facilities</h3>
                            </div>
                            <i className="fa-solid fa-arrow-right"></i>
                        </div>
                        <div className="facilities-list-modern">
                            {dashboardData.popularFacilities.length > 0 ? (
                                dashboardData.popularFacilities.map((facility, index) => (
                                    <div key={facility.facility_name} className="facility-item-modern">
                                        <div className="facility-rank-modern">{index + 1}</div>
                                        <div className="facility-info-modern">
                                            <div className="facility-name-modern">{facility.facility_name}</div>
                                            <div className="facility-progress">
                                                <div className="facility-progress-bar">
                                                    <div className="facility-progress-fill" style={{ 
                                                        width: `${(facility.booking_count / dashboardData.popularFacilities[0].booking_count) * 100}%` 
                                                    }}></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="facility-count-modern">
                                            <span className="count-number">{facility.booking_count}</span>
                                            <span className="count-label">bookings</span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">No facility data available</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="dashboard-right-col">
                    {/* Status Distribution - Donut Style */}
                    <div className="dashboard-card-modern status-card">
                        <div className="card-header-modern">
                            <div className="header-title">
                                <i className="fa-solid fa-chart-pie"></i>
                                <h3>Booking Status Overview</h3>
                            </div>
                        </div>
                        <div className="status-grid-modern">
                            {Object.entries(dashboardData.statusDistribution).map(([status, count]) => (
                                count > 0 && (
                                    <div key={status} className="status-item-modern">
                                        <div className="status-info-modern">
                                            <div className="status-badge" style={{ backgroundColor: getStatusColor(status) }}></div>
                                            <span className="status-name">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                                        </div>
                                        <div className="status-value-modern">
                                            <span className="status-count-modern">{count}</span>
                                            <span className="status-percent">
                                                {Math.round((count / dashboardData.stats.totalBookings) * 100)}%
                                            </span>
                                        </div>
                                        <div className="status-progress">
                                            <div className="status-progress-bar">
                                                <div className="status-progress-fill" style={{ 
                                                    width: `${(count / dashboardData.stats.totalBookings) * 100}%`,
                                                    backgroundColor: getStatusColor(status)
                                                }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    </div>

                    {/* Recent Bookings - Timeline Style */}
                    <div className="dashboard-card-modern recent-card">
                        <div className="card-header-modern">
                            <div className="header-title">
                                <i className="fa-solid fa-clock"></i>
                                <h3>Recent Activity</h3>
                            </div>
                            <button className="view-link" onClick={() => window.location.href = '/admin?tab=bookings'}>
                                View All <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                        <div className="timeline-list">
                            {dashboardData.recentBookings.length > 0 ? (
                                dashboardData.recentBookings.map((booking, idx) => (
                                    <div key={booking.id} className="timeline-item">
                                        <div className="timeline-marker" style={{ backgroundColor: getStatusColor(booking.status) }}></div>
                                        <div className="timeline-content">
                                            <div className="timeline-title">{booking.facility_name || booking.location || 'Booking'}</div>
                                            <div className="timeline-date">{formatDate(booking.created_at)}</div>
                                        </div>
                                        <div className={`timeline-status status-${booking.status}`}>
                                            {booking.status || 'pending'}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">No recent bookings</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Section - Equipment Inventory */}
            <div className="dashboard-card-modern inventory-card-full">
                <div className="card-header-modern">
                    <div className="header-title">
                        <i className="fa-solid fa-toolbox"></i>
                        <h3>Equipment & Inventory Status</h3>
                    </div>
                    <i className="fa-solid fa-arrow-right"></i>
                </div>
                <div className="inventory-grid-modern">
                    {dashboardData.equipmentInventory.length > 0 ? (
                        dashboardData.equipmentInventory.slice(0, 4).map(item => (
                            <div key={item.id} className="inventory-card-modern">
                                <div className="inventory-icon">
                                    <i className="fa-solid fa-gear"></i>
                                </div>
                                <div className="inventory-details">
                                    <div className="inventory-name-modern">{item.name}</div>
                                    <div className="inventory-category-modern">{item.category}</div>
                                </div>
                                <div className={`inventory-stock-modern ${item.available > 0 ? 'in-stock' : 'out-stock'}`}>
                                    {item.available > 0 ? `${item.available} available` : 'Out of stock'}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-data">No equipment data available</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;